import _ from 'lodash';
import Fuse from 'fuse.js';
import { lsApiService, lsErrorService, lsImageHelpers, lsToastService } from 'ls-vue-common';
import {
  PROPERTIES__GET_PROPERTIES_AND_SCHEDULES,
  SCHEDULES__MUTATE_SCHEDULE,
  SUPPORT__CANCEL_STANDALONE,
  SUPPORT__CHANGE_FREQUENCY,
  SUPPORT__DAY_OF_WEEK_AVAILABILITY,
  SUPPORT__GET_SEARCHABLE_ACTIONS,
  SUPPORT__MUTATE_DAY_OF_WEEK_AVAILABILITY,
  SUPPORT__MUTATE_LOADING_STATUS,
  SUPPORT__MUTATE_SEARCH_QUERY,
  SUPPORT__MUTATE_SEARCHABLE_ACTIONS,
  SUPPORT__ONE_TIME_RESCHEDULE,
  SUPPORT__PAUSE_SERVICE,
  SUPPORT__REPORT_DAMAGE,
  SUPPORT__RESET,
  SUPPORT__SEARCHABLE_ACTIONS_FILTERED_BY_SEARCH_QUERY,
  SUPPORT__SKIP_MOW,
  SUPPORT__SUBMIT_MESSAGE,
} from '@/store/types';
import moment from 'moment';
import { lsModuleHelpers } from '../helpers';
import { normalizeFuseResults } from './support.helpers';
import { SUPPORTED_ACTIONS } from './support.constants';
import { getBrandName } from '@/helpers';

const createInitialState = () => {
  return {
    loadingStatus: {
      isLoading: false,
      isUpdating: false,
    },
    searchableActions: [],
    searchQuery: '',
    dayOfWeekAvailability: {},
  };
};

export const mutations = {
  [SUPPORT__RESET](state) {
    lsModuleHelpers.resetState({ state, createInitialState });
  },

  [SUPPORT__MUTATE_LOADING_STATUS](state, payload) {
    _.assign(state.loadingStatus, payload);
  },
  [SUPPORT__MUTATE_SEARCHABLE_ACTIONS](state, payload) {
    state.searchableActions = payload;
  },
  [SUPPORT__MUTATE_SEARCH_QUERY](state, searchQuery) {
    state.searchQuery = searchQuery;
  },
  [SUPPORT__MUTATE_DAY_OF_WEEK_AVAILABILITY](state, payload) {
    state.dayOfWeekAvailability = {
      ...state.dayOfWeekAvailability,
      ...payload,
    };
  },
};

export const actions = {
  [SUPPORT__SUBMIT_MESSAGE](
    { commit },
    {
      message,
      onSuccess,
      topic = 'My Service',
      issue = `Message ${getBrandName()}`,
      noSideEffects = false,
      useToast = true,
    }
  ) {
    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    return lsApiService
      .request({
        method: 'POST',
        route: `/support`,
        payload: {
          message: `Topic: ${topic}\nIssue: ${issue}\n\nMessage: ${message}`,
        },
      })
      .then(() => {
        onSuccess(!noSideEffects);
        if (useToast && !noSideEffects)
          lsToastService.open({
            message: "Thanks, we've received your message. We will get back to you soon.",
          });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SUPPORT__PAUSE_SERVICE]({ commit, dispatch, rootState }, { properties }) {
    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/pause`,
        payload: {
          properties: _.reduce(
            properties,
            (result, selected, property_id) => {
              if (selected) {
                result.push({ property_id });
              }
              return result;
            },
            []
          ),
        },
      })
      .then(({ data }) => {
        dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });
        return data;
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SUPPORT__CANCEL_STANDALONE](
    { commit, dispatch, rootState },
    {
      schedules,
      reason,
      notes,
      plans_to_hire_other_provider,
      satisfaction,
      notes_good,
      notes_improve,
    }
  ) {
    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        apiVersion: 'v2',
        route: `/customers/${customer_id}/schedules/cancel`,
        payload: {
          schedule_ids: schedules,
          reason,
          notes,
          plans_to_hire_other_provider,
          satisfaction,
          notes_good,
          notes_improve,
        },
      })
      .then(({ data }) => {
        dispatch(PROPERTIES__GET_PROPERTIES_AND_SCHEDULES, { forceRefresh: true });
        return data;
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SUPPORT__REPORT_DAMAGE](
    { commit, rootState },
    {
      property_id,
      damage_occurred_at,
      damage_details,
      damage_cost,
      resolution_desired,
      third_parties,
      pendingImages,
      onSuccess,
    }
  ) {
    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;
    const imagesWithMeta = lsImageHelpers.generateImagesWithMetaPayload({
      images: pendingImages,
      tag: 'support.damaged_property',
      namePrefix: 'Support - Damaged Property',
    });

    const data = new FormData();
    _.forEach(imagesWithMeta, (imageAndMeta, idx) => {
      const { image, meta } = imageAndMeta;
      // need to include image obj with name, type, uri for file upload to work
      data.append(`images[${idx}]`, image);
      data.append(`images_meta[${idx}][tag]`, meta.tag);
      data.append(`images_meta[${idx}][title]`, meta.title);
    });

    data.append('damage_occurred_at', moment(damage_occurred_at).format('YYYY-MM-DD'));
    data.append('damage_details', damage_details);
    data.append('damage_cost', damage_cost);
    data.append('resolution_desired', resolution_desired);
    data.append('third_parties', third_parties ? 1 : 0);

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/damages`,
        payload: data,
      })
      .then(() => {
        onSuccess();
        lsToastService.open({
          message:
            "We're so sorry that you've experienced this issue. We'll reach out to your crew and begin working towards a resolution.",
          duration: 5000,
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SUPPORT__CHANGE_FREQUENCY](
    { commit, rootState },
    { property_id, active_mowing_schedule_id, new_cycle, onSuccess, immediate = undefined }
  ) {
    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        apiVersion: 'v2',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${active_mowing_schedule_id}/cycle`,
        payload: {
          new_cycle,
          ...(typeof immediate !== 'undefined' ? { immediate } : {}),
        },
      })
      .then(({ data }) => {
        onSuccess();
        commit(SCHEDULES__MUTATE_SCHEDULE, data.schedule);
        lsToastService.open({
          message: 'Your mowing frequency has been changed',
          type: 'is-success',
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SUPPORT__SKIP_MOW](
    { commit, rootState },
    { property_id, active_mowing_schedule_id, onSuccess }
  ) {
    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${active_mowing_schedule_id}/skip`,
      })
      .then(({ data }) => {
        commit(SCHEDULES__MUTATE_SCHEDULE, data.schedule);
        onSuccess();
        lsToastService.open({
          message: 'Your next service has been skipped',
          type: 'is-success',
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SUPPORT__GET_SEARCHABLE_ACTIONS]({ commit, rootState }) {
    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        route: `/customers/${customer_id}/support-actions`,
      })
      .then(({ data }) => {
        const supportedActions = data.actions.filter((item) => SUPPORTED_ACTIONS[item.action]);
        const index = supportedActions.findIndex((opt) => opt.action === 'contact_lawnstarter');

        if (index !== -1) {
          supportedActions[index].display = `Contact ${getBrandName()} Support`;
        }

        commit(SUPPORT__MUTATE_SEARCHABLE_ACTIONS, supportedActions);
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SUPPORT__ONE_TIME_RESCHEDULE](
    { commit, rootState },
    {
      property_id,
      schedule_id,
      date,
      reason,
      reason_details = null,
      requested_is_contractor_flexible,
      onSuccess,
    }
  ) {
    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isUpdating: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        method: 'POST',
        route: `/customers/${customer_id}/properties/${property_id}/schedules/${schedule_id}/one-time-service-day-change`,
        payload: {
          date,
          reason,
          reason_details,
          requested_is_contractor_flexible,
        },
      })
      .then(() => {
        onSuccess();
        lsToastService.open({
          message: "Thanks, we've received your request.",
          type: 'is-success',
        });
      })
      .catch((error) => {
        lsErrorService.log({ error, showAlert: true });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isUpdating: false });
      });
  },

  [SUPPORT__DAY_OF_WEEK_AVAILABILITY]({ commit, state, rootState }, { property_id }) {
    if (state.dayOfWeekAvailability[property_id]) {
      return Promise.resolve();
    }

    commit(SUPPORT__MUTATE_LOADING_STATUS, {
      isLoading: true,
    });

    const { customer_id } = rootState.auth.user;

    return lsApiService
      .request({
        route: `/customers/${customer_id}/properties/${property_id}/day-of-week-availability`,
      })
      .then(({ data }) => {
        commit(SUPPORT__MUTATE_DAY_OF_WEEK_AVAILABILITY, { [property_id]: data });
      })
      .catch((error) => {
        lsErrorService.log({ error });
      })
      .finally(() => {
        commit(SUPPORT__MUTATE_LOADING_STATUS, { isLoading: false });
      });
  },
};
export const getters = {
  [SUPPORT__SEARCHABLE_ACTIONS_FILTERED_BY_SEARCH_QUERY](state) {
    const options = {
      shouldSort: true,
      threshold: 0.1,
      location: 0,
      distance: 100,
      maxPatternLength: 32,
      minMatchCharLength: 2,
      keys: ['display', 'tags'],
    };
    const fuse = new Fuse(state.searchableActions, options); // "list" is the item array
    let results = state.searchQuery.length
      ? fuse.search(state.searchQuery)
      : state.searchableActions;
    return normalizeFuseResults(results);
  },
};

export const support = {
  state: createInitialState,
  mutations,
  actions,
  getters,
};
